import './BundlesInfoPage.scss';

import {
  ActionSheetButton,
  IonActionSheet,
  IonButton,
  IonCol,
  IonContent,
  IonFab,
  IonFabButton,
  IonGrid,
  IonIcon,
  IonPage,
  IonProgressBar,
  IonRow,
} from '@ionic/react';
import { createNextState } from '@reduxjs/toolkit';
import { arrowUndoCircleOutline, menuOutline, paperPlane, trash } from 'ionicons/icons';
import moment from 'moment';
import { FC, useCallback, useMemo, useReducer, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { DqText } from 'src/components/dq-text/DqText';
import DesktopDetailPageHeader from 'src/components/reusable-components/desktop-header/DesktopDetailPageHeader';
import MobileDetailPageHeader from 'src/components/reusable-components/mobile-detail-page-header/MobileDetailPageHeader';
import { BleConnectionState } from 'src/redux/ble/constants';
import { selectBleConnectionState, selectBleIsLocked, selectBleWriteBundle } from 'src/redux/ble/selectors';
import { BundleDetails } from '../../components/bundle-details/BundleDetails';
import ShareBundleModal from '../../components/bundles/bundle-sharing/ShareBundleModal';
import { YesNoModal } from '../../components/reusable-components/yes-no-modal/YesNoModal';
import { UnshareModal } from './UnshareModal';
import { BundleMessages } from '../../intl/BundleMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import {
  Bundle,
  BundleDisplayPermissions,
  CurrentModal,
  Permission,
  ScreenState,
  ShareBundleData,
} from '../../models/bundle';
import { Organization, OrganizationStatus } from '../../models/organization';
import { canDeleteBundle, canShareBundle, usePolicy } from '../../models/policies';
import { useDeleteBundleMutation, useGetBundlesQuery, useUpdateBundleMutation } from '../../redux/api/bundles';
import { useListOrganizationsV2Query } from '../../redux/api/organizations';
import { selectAuthUserInfo } from '../../redux/auth/selectors';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { FeatureGate } from '../../utils/feature-flags/FeatureFlags';
import { getOrgName } from '../../utils/helpers/referenceHelpers';
import { useSocketGet } from '../../utils/socket';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { BundleListPageActions } from './BundlesListPage';
import { useBundleActions } from './useBundleActions';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import { useHasBluetoothLE, useIsMobile } from 'src/utils/helpers/platformHelpers';
import { ToastType, useToast } from 'src/components/reusable-components/useToast';
import { useGA4 } from '../../utils/ga4';

const initialScreenState: ScreenState = {
  currentModal: undefined,
  shareTarget: undefined,
  orgFilter: {
    statuses: ['active'] as OrganizationStatus[],
    pageNumber: 0,
    pageSize: 1000,
  },
};

type ScreenAction =
  | { type: 'BundleShare:Init' }
  | { type: 'BundleShare:Success' }
  | { type: 'BundleShare:Error' }
  | { type: 'BundleUnshare:Init' }
  | { type: 'BundleUnshare:Success' }
  | { type: 'BundleUnshare:Error' }
  | { type: 'Modal:Closed'; modal: CurrentModal };
function screenReducer(state: ScreenState, action: ScreenAction): ScreenState {
  return createNextState(state, draft => {
    switch (action.type) {
      case 'BundleShare:Init':
        draft.currentModal = 'Share';
        break;

      case 'BundleUnshare:Init':
        draft.currentModal = 'Unshare';
        break;

      case 'BundleShare:Success':
      case 'BundleShare:Error':
      case 'BundleUnshare:Success':
      case 'BundleUnshare:Error':
        draft.currentModal = undefined;
        break;
      case 'Modal:Closed':
        if (draft.currentModal === action.modal) {
          draft.currentModal = undefined;
        }
        break;
    }
  });
}
export const BundlesInfoPage: FC = () => {
  document.title = 'Bundle Detail'; // Purposely English only for Google Analytics
  useGA4();

  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { isLimited } = useLimitedMode();
  const { formatMessage } = useIntl();
  const [deleteWarningOpen, setDeleteWarningOpen] = useState<boolean>(false);
  const [screenState, dispatch] = useReducer(screenReducer, initialScreenState);
  const [showActionSheet, setShowActionSheet] = useState<boolean>(false);
  const { data: organizations } = useListOrganizationsV2Query(screenState.orgFilter);
  const bundleWriteProgress = useSelector(selectBleWriteBundle);
  const isBleLocked = useSelector(selectBleIsLocked);
  const isConnected = useSelector(selectBleConnectionState) === BleConnectionState.CONNECTED;
  const isControllerActionDisabled = isBleLocked || !isConnected;
  const isMobile = useIsMobile();
  const hasBluetooth = useHasBluetoothLE();
  const [present] = useToast();

  const { data: bundleFetchResult, isLoading, refetch } = useGetBundlesQuery(params.id);
  const [updatedBundle] = useUpdateBundleMutation();
  const [deleteBundle] = useDeleteBundleMutation();
  const {
    writeBundle: writeBundleToController,
    deleteBundle: deleteBundleFromController,
    installedBundleFile,
  } = useBundleActions(bundleFetchResult);
  const onControllerActionPress = installedBundleFile ? deleteBundleFromController : writeBundleToController;

  const controllerAction = useMemo(() => {
    if (!hasBluetooth) return undefined;

    const result = {
      text: installedBundleFile
        ? formatMessage(BundleMessages.uninstallButton)
        : formatMessage(BundleMessages.installButton),
      color: installedBundleFile ? 'secondary' : 'primary',
      onPress: onControllerActionPress,
      disabled: isControllerActionDisabled,
    };
    return result;
  }, [hasBluetooth, installedBundleFile, formatMessage, onControllerActionPress, isControllerActionDisabled]);

  const bundleWritePercentage = useMemo(() => {
    if (!bundleWriteProgress) return undefined;
    if (bundleWriteProgress.bundleId !== bundleFetchResult?._id) return undefined;
    return bundleWriteProgress.writtenBytes / bundleWriteProgress.totalBytes;
  }, [bundleFetchResult?._id, bundleWriteProgress]);

  const isShared = (bundleFetchResult?.permissions?.length ?? 0) > 0;

  /** determines if user can share with a given organization */
  const canShareWithOrganization = (permissions: Permission[]) => {
    return function filterOrganization(organization: Organization) {
      const alreadyShared = !!permissions.find(p => p.principal === organization._id);
      return !alreadyShared;
    };
  };
  const sharableOrganizations = useMemo(
    () => organizations?.filter(canShareWithOrganization(bundleFetchResult?.permissions ?? [])),
    [organizations, bundleFetchResult?.permissions],
  );

  const displayPermissions = {
    showDeleteActions: usePolicy(canDeleteBundle, bundleFetchResult),
    showShareActions: usePolicy(canShareBundle, bundleFetchResult),
  } as BundleDisplayPermissions;

  const onDeleteBundle = async (isYes: boolean) => {
    setDeleteWarningOpen(false);
    if (!isYes) {
      return;
    }
    try {
      // mutations have some middleware that resolves errors
      // behind the scenes and doesn't allow us to handle API errors in our own way.
      // unwrapping will cause data to resolve, or an error to be thrown
      await deleteBundle(params.id).unwrap();
      history.replace(AppRouteConstants.BundlesList, { action: BundleListPageActions.BundleDeletedSuccessful });
    } catch (error) {
      console.warn('error', error);
      history.replace(AppRouteConstants.BundlesList, { action: BundleListPageActions.BundleDeleteFailed });
    }
  };
  const actionSheetButtons = useMemo(() => {
    const buttons: ActionSheetButton[] = [];

    // Can only delete bundles, owned by your org in any role other than a user.
    if (displayPermissions.showDeleteActions) {
      buttons.push({
        cssClass: 'bundle-info-action-button',
        text: formatMessage(BundleMessages.deleteBundleButtonText),
        handler: () => {
          setDeleteWarningOpen(true);
        },
      });
    }
    if (displayPermissions.showShareActions) {
      if (isShared) {
        buttons.push({
          cssClass: 'bundle-info-action-button',
          text: formatMessage(BundleMessages.unshareBundle),
          handler: () => {
            dispatch({ type: 'BundleUnshare:Init' });
            setShowActionSheet(false);
          },
        });
      }
      buttons.push({
        cssClass: 'bundle-info-action-button',
        text: formatMessage(BundleMessages.shareBundle),
        handler: () => {
          dispatch({ type: 'BundleShare:Init' });
          setShowActionSheet(false);
        },
      });
    }

    return buttons;
  }, [displayPermissions.showDeleteActions, displayPermissions.showShareActions, formatMessage, isShared]);

  const raiseToast = useCallback(
    (type: ToastType, header: string) => {
      present({ message: header }, type);
    },
    [present],
  );

  const delay = (ms: number) => {
    return new Promise(res => setTimeout(res, ms));
  };
  const handleShareReportSubmit = async (values: ShareBundleData) => {
    try {
      if (!bundleFetchResult) throw new Error('No bundle loaded!');
      const orgName = values.organizationId ? getOrgName(values.organizationId, organizations ?? []) : null;

      const request = {
        ...bundleFetchResult,
        permissions: [
          ...(bundleFetchResult?.permissions ?? []),
          {
            type: 'org',
            principal: values.organizationId,
            principalName: orgName,
            permissions: {
              readDetails: true,
            },
          },
        ] as Permission[],
      };

      await updatedBundle(request).unwrap();
      raiseToast('success', formatMessage(BundleMessages.shareBundleSuccessMessage, { orgName }));
      dispatch({ type: 'BundleShare:Success' });
      refetch();
    } catch (err) {
      console.warn('error', err);
      raiseToast('danger', formatMessage(BundleMessages.shareFailureMessage));
      dispatch({ type: 'BundleShare:Error' });
      refetch();
    }
  };

  const handleUnshareReport = useCallback(
    async (unshared: string[]) => {
      if (unshared.length === 0 || !bundleFetchResult) {
        dispatch({ type: 'Modal:Closed', modal: 'Unshare' });
        return;
      }
      const removed = bundleFetchResult?.permissions?.filter((p: Permission) => unshared.includes(p.principal));
      const orgNames = removed?.map((r: Permission) => r.principalName).join(', ');
      const permissions = bundleFetchResult?.permissions?.filter((p: Permission) => !unshared.includes(p.principal));
      const request = {
        ...bundleFetchResult,
        permissions: [...(permissions ?? [])] as Permission[],
      };
      try {
        await updatedBundle(request).unwrap();
        raiseToast('success', formatMessage(BundleMessages.unshareBundleSuccessMessage, { orgNames }));
        //Delay the refetch of the bundle by 5 seconds the toast message enough time to be displayed properly
        dispatch({ type: 'BundleUnshare:Success' });
        await delay(5000);
        refetch();
      } catch (e) {
        console.warn('error', e);
        raiseToast('danger', formatMessage(BundleMessages.unshareFailureMessage));
        dispatch({ type: 'BundleUnshare:Error' });
      }
    },
    [bundleFetchResult, updatedBundle, raiseToast, formatMessage, refetch],
  );

  function useBundleCreatedInfo(bundle: Bundle | undefined, formatMessage: IntlShape['formatMessage']) {
    const user = useSelector(selectAuthUserInfo);
    const shareRecord = bundle?.permissions?.find(p => p.principal === user!.org);

    // if shared, we dont need to bother trying to look up the created by user (in fact permissions will probably reject it)
    const createdById = shareRecord ? undefined : bundle?.createdBy;
    const createdByOrg = shareRecord ? getOrgName(bundle?.organizationId, organizations) : undefined;
    // need to use useSocketGet because it caches the data for us, and annotations might need the cached data
    const { item: createdBy } = useSocketGet(SocketContextPortal, 'users', createdById);
    const { item: org } = useSocketGet(SocketContextPortal, 'organizations', createdByOrg);

    if (!bundle) {
      return '';
    }

    if (shareRecord) {
      const date = moment(new Date(bundle.createdAt)).format('MMM D, YYYY');
      return formatMessage(BundleMessages.bundleInfoSubHeading, {
        userName: org?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder),
        date,
      });
    }

    const userName = !createdBy ? 'unknown' : `${createdBy.given_name} ${createdBy.family_name}`;

    const date = moment(new Date(bundle.createdAt)).format('MMM D, YYYY');
    return formatMessage(BundleMessages.bundleInfoSubHeading, {
      userName: userName,
      date,
    });
  }
  const formattedName = useBundleCreatedInfo(bundleFetchResult, formatMessage);
  return (
    <IonPage className="bundle-info-page">
      {isMobile ? (
        <MobileDetailPageHeader title={bundleFetchResult?.name} />
      ) : (
        <DesktopDetailPageHeader
          left
          heading={bundleFetchResult?.name}
          subheading={formattedName}
          right={
            <>
              {displayPermissions.showShareActions && (
                <IonButton onClick={() => dispatch({ type: 'BundleShare:Init' })}>
                  <IonIcon slot="icon-only" color="dark" size="large" icon={paperPlane} />
                </IonButton>
              )}
              {isShared && displayPermissions.showShareActions && (
                <IonButton onClick={() => dispatch({ type: 'BundleUnshare:Init' })}>
                  <IonIcon slot="icon-only" color="dark" size="large" icon={arrowUndoCircleOutline} />
                </IonButton>
              )}
              {displayPermissions.showDeleteActions && (
                <IonButton onClick={() => setDeleteWarningOpen(true)} disabled={isLimited}>
                  <IonIcon slot="icon-only" color="dark" size="large" icon={trash} />
                </IonButton>
              )}
            </>
          }
        />
      )}
      <FeatureGate featureName={'BUNDLES'}>
        <IonContent className="bundle-info-content">
          {isMobile && !isLoading && (
            <IonGrid className="ion-margin-bottom ion-no-padding">
              {bundleWritePercentage !== undefined && (
                <IonRow className="ion-margin-bottom ion-padding-bottom">
                  <IonCol>
                    {formatMessage(BundleMessages.installProgressLabel)}
                    <IonProgressBar value={bundleWritePercentage} color="secondary" />
                  </IonCol>
                </IonRow>
              )}
              <IonRow className="details-heading">
                <IonCol>
                  <DqText variant="h1">{bundleFetchResult?.name}</DqText>
                </IonCol>
                {controllerAction && (
                  <IonCol size="auto">
                    <IonButton
                      disabled={controllerAction.disabled}
                      onClick={controllerAction.onPress}
                      color={controllerAction.color}
                      size="small"
                    >
                      {controllerAction.text}
                    </IonButton>
                  </IonCol>
                )}
              </IonRow>
              <IonRow className="job-info-heading-row">
                <DqText>{formattedName}</DqText>
              </IonRow>
            </IonGrid>
          )}
          {bundleFetchResult && <BundleDetails bundle={bundleFetchResult} organizations={organizations ?? []} />}
          {isMobile && !!actionSheetButtons && actionSheetButtons.length > 0 && (
            <>
              <IonFab vertical="bottom" horizontal="end" slot="fixed">
                <IonFabButton onClick={() => setShowActionSheet(true)}>
                  <IonIcon icon={menuOutline} />
                </IonFabButton>
              </IonFab>
              <IonActionSheet
                isOpen={showActionSheet}
                onDidDismiss={() => setShowActionSheet(false)}
                buttons={actionSheetButtons}
              />
            </>
          )}
          <ShareBundleModal
            isOpen={screenState.currentModal === 'Share'}
            onClose={() => dispatch({ type: 'Modal:Closed', modal: 'Share' })}
            organizations={sharableOrganizations ?? []}
            submitForm={(e: ShareBundleData) => handleShareReportSubmit(e)}
          />
          <UnshareModal
            isOpen={screenState.currentModal === 'Unshare'}
            permissions={bundleFetchResult?.permissions}
            onSubmit={handleUnshareReport}
            onClose={async () => dispatch({ type: 'Modal:Closed', modal: 'Unshare' })}
            title={formatMessage(BundleMessages.unshareBundle)}
            description={formatMessage(BundleMessages.unshareBundleDescription)}
          />
          <YesNoModal
            isOpen={deleteWarningOpen}
            title={formatMessage(BundleMessages.deleteBundleTitle)}
            description={formatMessage(GenericMessages.deleteWarning)}
            yesButtonText={formatMessage(BundleMessages.deleteBundleButtonText)}
            yesButtonColor="danger"
            noButtonText={formatMessage(GenericMessages.cancelButtonLabel)}
            displayCloseButton={true}
            close={(isYes: boolean) => onDeleteBundle(isYes)}
          />
        </IonContent>
      </FeatureGate>
    </IonPage>
  );
};
